import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { SchedulePreviewAppointment } from '@grow-therapy-team/seedling-components';

import { logger } from '../../datadog/logger';
import { FORM_LIST_INTAKE_FORM_TITLE } from '../measures/constants';
import { useLazyGetFormsByPatientShortId } from '../useGetFormsByPatientShortId';
import { formDataByPatientShortIdAtom } from './state';
import { useOpenClientFormsCallback } from './useOpenClientFormsCallback';
import { useOpenClientInfoDrawerForAppointment } from './useOpenClientInfoDrawerForAppointment';

type OpenIntakeFormResponseArgs =
  | {
      appointment: SchedulePreviewAppointment['node'];
      patientShortId?: never;
    }
  | {
      appointment?: never;
      patientShortId: string;
    };

/**
 * Ensures patient forms are loaded, then opens the intake form response in the
 * client information drawer. When an appointment is provided (e.g. Prep card),
 * also opens the drawer for that appointment first.
 */
export function useOpenIntakeFormResponseCallback() {
  const openClientInfoDrawerForAppointment = useOpenClientInfoDrawerForAppointment();
  const openClientForms = useOpenClientFormsCallback();
  const [fetchForms] = useLazyGetFormsByPatientShortId();
  const getFormDataByPatient = useAtomCallback(useCallback((get) => get(formDataByPatientShortIdAtom), []));

  return useCallback(
    async ({ appointment, patientShortId }: OpenIntakeFormResponseArgs) => {
      const resolvedPatientShortId = appointment?.patient?.shortId ?? patientShortId;
      if (!resolvedPatientShortId) {
        logger.error('Missing patient short ID when opening intake form response');
        return;
      }

      if (appointment) {
        openClientInfoDrawerForAppointment(appointment);
      }

      const formDataByPatient = getFormDataByPatient();
      if (!formDataByPatient[resolvedPatientShortId]) {
        const { error } = await fetchForms({
          variables: { patientShortId: resolvedPatientShortId },
        });
        if (error) {
          logger.error(
            'Unable to fetch patient forms before opening intake form response',
            { patientShortId: resolvedPatientShortId },
            error,
          );
          return;
        }
      }

      openClientForms(FORM_LIST_INTAKE_FORM_TITLE);
    },
    [fetchForms, getFormDataByPatient, openClientForms, openClientInfoDrawerForAppointment],
  );
}
